import { useCallback } from 'react';
import { CreateActivityFormData, TravelContactData } from '../../../../../../types/activity.types';
import { UI_TO_API_FIELD_MAP } from '../../../../../../types/activity.config';
import { combineDateAndTime } from '../../../../../../utils/dateUtils';
import { parseAmountSpentForApi } from '../../../../../../utils/amountUtils';
import { convertAddressToApiFormat } from '../../../utils/addressUtils';

/**
 * Hook for converting form data to API payload
 * @param tripId - The trip ID to associate the activity with
 * @returns Object with convertToApiPayload function
 */
export const useActivityPayload = (tripId: number) => {
  const convertToApiPayload = useCallback(
    (values: CreateActivityFormData) => {
      const activityType = values.activityType;
      const apiData: any = {};

      const createTravelContact = (): TravelContactData | null => {
        const hasEmail = values.email && values.email.trim() !== '';
        const hasPhone = values.phone && values.phone.trim() !== '';
        const hasLink = values.link && values.link.trim() !== '';

        if (!hasEmail && !hasPhone && !hasLink) {
          return null;
        }

        let contactName = '';
        switch (activityType) {
          case 'Flight':
            contactName = values.airline_details?.name || values.airline || 'Flight Contact';
            break;
          case 'Hotel':
            contactName = values.name || 'Hotel Contact';
            break;
          case 'CarRental':
            contactName = values.name || 'Car Rental Contact';
            break;
          default:
            contactName = values.name || `${activityType} Contact`;
        }

        return {
          name: contactName,
          email: hasEmail ? values.email : null,
          phone_number: hasPhone ? values.phone : null,
          link: hasLink ? values.link : null,
        };
      };

      const travelContact = createTravelContact();
      if (travelContact) {
        apiData.travel_contact = travelContact;
        console.log('Created travel_contact:', travelContact);
      }

      Object.keys(values).forEach(uiField => {
        const apiField = UI_TO_API_FIELD_MAP[uiField] || uiField;
        const value = values[uiField as keyof CreateActivityFormData];

        if (
          value !== '' &&
          value !== null &&
          value !== undefined &&
          !uiField.includes('_details') &&
          !['documentName', 'documentTags', 'attachments', 'activityType'].includes(uiField) &&
          !uiField.includes('Date') &&
          !uiField.includes('Time') &&
          !uiField.includes('sameAs') &&
          !['airline', 'flightFrom', 'flightTo'].includes(uiField) &&
          !['email', 'phone', 'link'].includes(uiField)
        ) {
          switch (uiField) {
            case 'amountSpent':
              apiData.amount_spend = parseAmountSpentForApi(value as string | number);
              break;
            case 'numberOfPassengers':
              apiData.number_of_passengers = Number(value) || 0;
              break;
            default:
              apiData[apiField] = value;
          }
        }
      });

      if (values.airline_details) {
        apiData.airline = values.airline_details.name;
        apiData.airline_iata_code = values.airline_details.iata_code;
        console.log('✈️ Airline details:', values.airline_details);
      } else if (values.airline) {
        apiData.airline_iata_code = values.airline;
        console.log('Only airline iata_code available:', values.airline);
      }

      if (values.flightFrom_details) {
        apiData.flight_from = values.flightFrom_details.iata_code;
        apiData.flight_from_airport_name = values.flightFrom_details.name;
        console.log('Flight from details:', values.flightFrom_details);
      } else if (values.flightFrom) {
        apiData.flight_from = values.flightFrom;
        console.log('Only flight from iata_code available:', values.flightFrom);
      }

      if (values.flightTo_details) {
        apiData.flight_to = values.flightTo_details.iata_code;
        apiData.flight_to_airport_name = values.flightTo_details.name;
        console.log('🛬 Flight to details:', values.flightTo_details);
      } else if (values.flightTo) {
        apiData.flight_to = values.flightTo;
        console.log('Only flight to iata_code available:', values.flightTo);
      }
      const dateTimeFields = [
        { date: 'departureDate', time: 'departureTime', api: 'departure_date_time' },
        { date: 'arrivalDate', time: 'arrivalTime', api: 'arrival_date_time' },
        { date: 'startDate', time: 'startTime', api: 'start_date_time' },
        { date: 'endDate', time: 'endTime', api: 'end_date_time' },
        { date: 'checkInDate', time: 'checkInTime', api: 'check_in_date_time' },
        { date: 'checkOutDate', time: 'checkOutTime', api: 'check_out_date_time' },
        { date: 'date', time: 'time', api: 'start_date_time' },
      ];

      dateTimeFields.forEach(({ date, time, api }) => {
        const dateValue = values[date as keyof CreateActivityFormData] as string;
        const timeValue = values[time as keyof CreateActivityFormData] as string;

        if (dateValue && timeValue) {
          apiData[api] = combineDateAndTime(dateValue, timeValue);
        }
      });

      const addressFields = [
        { ui: 'address', api: 'address' },
        { ui: 'pickupAddress', api: 'pick_up_address' },
        { ui: 'dropOffAddress', api: 'drop_off_address' },
        { ui: 'departureAddress', api: 'departure_address' },
        { ui: 'arrivalAddress', api: 'arrival_address' },
        { ui: 'startingPortAddress', api: 'starting_port_address' },
        { ui: 'endingPortAddress', api: 'ending_port_address' },
      ];

      addressFields.forEach(({ ui, api }) => {
        const addressValue = values[ui as keyof CreateActivityFormData] as string;
        const detailsValue = values[`${ui}_details` as keyof CreateActivityFormData] as google.maps.places.PlaceResult;

        if (addressValue) {
          if (ui === 'dropOffAddress' && values.sameAsPickupAddress) {
            return;
          }
          if ((ui === 'endingPortAddress' || ui === 'endingPortName') && values.sameAsStartingPort) {
            return;
          }
          const addressData = convertAddressToApiFormat(addressValue, detailsValue);
          if (addressData) {
            apiData[api] = addressData;
          }
        } else {
          console.log(`Skipping ${ui} (no address value)`);
        }
      });
      if (activityType === 'CarRental' && values.officeAddress) {
        const officeAddressData = convertAddressToApiFormat(values.officeAddress, values.officeAddress_details);
        if (officeAddressData) {
          apiData.address = officeAddressData;
        }
      }

      if ((activityType === 'Parking' || activityType === 'Directions') && values.place) {
        const placeAddressData = convertAddressToApiFormat(
          values.place,
          values.address_details
        );
        if (placeAddressData) {
          if (!apiData.address) {
            apiData.address = placeAddressData;
          }
          apiData.place = values.place;
        }
      }

      if (values.departureLocation) {
        apiData.location_from_name = values.departureLocation;
      }
      if (values.arrivalLocation) {
        apiData.location_to_name = values.arrivalLocation;
      }
      if (values.departureStation) {
        apiData.location_from_name = values.departureStation;
      }
      if (values.arrivalStation) {
        apiData.location_to_name = values.arrivalStation;
      }
      if (values.startingPortName) {
        apiData.location_from_name = values.startingPortName;
      }
      if (values.endingPortName && !values.sameAsStartingPort) {
        apiData.location_to_name = values.endingPortName;
      }

      switch (activityType) {
        case 'CarRental':
          if (values.vehicleType) {
            apiData.transport_name = values.vehicleType;
          }
          break;
        case 'Cruise':
          if (values.shipName) {
            apiData.transport_name = values.shipName;
          }
          break;
        case 'Rail':
          if (values.trainNumber) {
            apiData.train = values.trainNumber;
          }
          if (values.class) {
            apiData.seat_type = values.class;
          }
          break;
      }

      const finalPayload = {
        tripId: tripId.toString(),
        activityType: activityType,
        activityData: apiData,
      };

      console.log('Final API payload:', finalPayload);
      return finalPayload;
    },
    [tripId]
  );

  return { convertToApiPayload };
};
